import * as React from "react";

import { cn } from "@/lib/utils";

export type BadgeTone = "neutral" | "info" | "caution" | "success" | "danger" | "agent";
export type BadgeEmphasis = "soft" | "solid";

interface BadgeProps extends React.ComponentProps<"span"> {
  tone?: BadgeTone;
  emphasis?: BadgeEmphasis;
}

/**
 * <Badge tone? emphasis?> — семантический маркер статуса / severity / kind.
 *
 * Шесть канонических тонов (neutral / info / caution / success / danger / agent).
 * Две силы: `soft` (дефолт, refined-quiet pastel) и `solid` (saturated,
 * для критических состояний — Risk.critical, expired, defect и т.п.).
 *
 * Цвета приходят из CSS-токенов `--tone-N-bg/fg` и `--tone-N-solid-bg/fg`
 * в `tokens.css` — менять оттенки **только там**, badges подхватят.
 *
 * Маппинг доменных enum'ов (ProjectStatus, RiskSeverity, etc.) → `BadgeTone`
 * централизован в `lib/badgeTones.ts`. См. `docs/frontend/patterns.md §«Badges»`.
 */
export function Badge({
  tone = "neutral",
  emphasis = "soft",
  className,
  ...props
}: BadgeProps): React.ReactElement {
  const toneCls = emphasis === "solid" ? `tone-${tone}-solid` : `tone-${tone}`;
  return (
    <span
      data-slot="badge"
      data-tone={tone}
      data-emphasis={emphasis}
      className={cn(
        // Типографика: 11px / weight 500 / letter-spacing 0.08em / uppercase
        // (CloudDesign refined-classic, май 2026). Высота через padding,
        // без min-height — компактный inline-tag.
        "inline-flex items-center gap-1 rounded-[var(--radius-chip)] px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap uppercase",
        toneCls,
        className,
      )}
      style={{ letterSpacing: "0.08em", ...((props.style as React.CSSProperties) ?? {}) }}
      {...props}
    />
  );
}
